import { Children } from 'react';
import styles from './Article.module.scss';

import { DateTime } from '@zapier/date';
import type { Dateable } from '@zapier/date';
import { type ColorName, Colors } from '../../../theme';
import { Avatar, AvatarsWithText, AvatarText } from '../../display/Avatar';
import { Heading, HeadingLevel } from '../../display/Heading';
import { Img } from '../../display/Img';
import { Link } from '../../navigation/Link';
import { Icon, type IconName } from '@/components/display/Icon';

type Author = {
  name: string;
  imageUrl?: string;
};

/**
 * enforce the following relationship between the author prop and the meta prop:
 *   - if no author prop exists, you cannot pass the meta prop
 *   - if author prop exists, you can pass OR omit the meta prop
 */
type AuthorMetaInteractionProps =
  | {
      meta?: never;
    }
  | { author: Author; meta?: React.ReactNode };

export type Props = {
  /**
   * The `src` to use for `Article`'s image.
   */
  imageUrl?: string;
  /**
   * Optional category assigned to `Article`.
   */
  category?: string;
  /**
   * URL to page containing other articles within `category`.
   * If `category` is supplied, this should be supplied, too.
   */
  categoryUrl?: string;
  /**
   * A label that renders as a small tag
   * with a background color and an icon
   * If supplied, will take the place of the category link
   */
  label?: string;
  /**
   * The icon that is displayed in the label.
   * @default "fileFilled"
   */
  labelIcon?: IconName;
  /**
   * The labelVariant determines the background-color of the label.
   * @default "neutral"
   */
  labelVariant?: 'primary' | 'secondary' | 'neutral';
  /**
   * Title of `Article`.
   */
  title: string;
  /**
   * A brief description of `Article` to complement `title`.
   */
  description?: React.ReactNode;
  /**
   * How to lay out `Article`. `card` lays out all content vertically
   * with the image above the text, while `row` lays the image out
   * to the side of the text.
   */
  layout: 'card' | 'row';
  /**
   * Meta info about the article, typically how long it takes
   * to read it and/or the date that it was published. Use
   * the `ArticleMeta` component with `ArticleReadTime` and/or
   * `ArticleDate` as its children to render the typical content.
   * Render custom children if those don't suffice.
   */
  meta?: React.ReactNode;
  /**
   * URL to where `Article` links.
   */
  url: string;
  /**
   * Information about the author of `Article`.
   */
  author?: Author;
  /**
   * @deprecated The `size` of `Article` which impacts its typography,
   * image size, spacing, etc. All Articles render as size medium now.
   */
  size?: 'small' | 'medium' | 'large';
} & AuthorMetaInteractionProps;

type LabelBackgroundConfig = {
  [key: string]: ColorName;
};

const labelBackgroundColorConfig: LabelBackgroundConfig = {
  primary: 'UiPrimaryWeakest',
  secondary: 'BrandPeach',
  neutral: 'GrayWarm3',
};

const ArticleTag = ({
  label,
  labelIcon = 'fileFilled',
  labelVariant = 'neutral',
}: Pick<Props, 'label' | 'labelIcon' | 'labelVariant'>) => {
  return (
    <span
      className={styles.label}
      style={{
        '--_zds-article-label-bgcolor':
          Colors[labelBackgroundColorConfig[labelVariant]],
      }}
      data-zds
    >
      {labelIcon && <Icon name={labelIcon} size={18} />}
      <span data-zds>{label}</span>
    </span>
  );
};

/**
 * Renders an article preview which contains a title, image, and publishing
 * information: date, category, and author details.
 */
export const Article = (props: Props) => {
  return (
    <article className={styles.root} data-layout={props.layout} data-zds>
      {props.imageUrl && (
        <div className={styles.image} data-layout={props.layout} data-zds>
          <div className={styles.imageInner} data-zds>
            <Img
              // `aria-hidden` and empty `alt` are applied since we do not describe
              // our images for screen readers. Understanding the content of the image
              // is not critical to understanding the content of the article.
              alt=""
              aria-hidden={true}
              aspectRatio="2:1"
              borderRadius="none"
              isBlock={true}
              loading="lazy"
              objectFit="cover"
              sizes="100vw"
              src={props.imageUrl}
            />
          </div>
        </div>
      )}
      <div
        className={styles.content}
        data-layout={props.layout}
        data-showimg={!!props.imageUrl}
        data-rownoimg={props.layout === 'row' && !props.imageUrl}
        data-zds
      >
        <HeadingLevel>
          <Link
            className={styles.title}
            color={null}
            href={props.url}
            data-lastelement={!props.author && !props.description}
          >
            <Heading>{props.title}</Heading>
          </Link>
        </HeadingLevel>
        {props.description && (
          <div
            className={styles.description}
            data-layout={props.layout}
            data-lastelement={!props.author}
            data-zds
          >
            {props.description}
          </div>
        )}
        {props.author && (
          <div className={styles.metaInfo}>
            <AvatarsWithText
              avatars={
                <Avatar
                  aria-hidden={true}
                  isBlock={true}
                  name={props.author.name}
                  size="medium"
                  url={props.author.imageUrl}
                />
              }
              multiline={true}
              size="medium"
            >
              <AvatarText aria-label={`by ${props.author.name}`} kind="heavy">
                {props.author.name}
              </AvatarText>
              {props.meta}
            </AvatarsWithText>
          </div>
        )}
        {props.label && (
          <ArticleTag
            label={props.label}
            labelIcon={props.labelIcon}
            labelVariant={props.labelVariant}
          />
        )}
        {props.category && props.categoryUrl && !props.label && (
          <div className={styles.category} data-zds>
            <Link
              aria-label={`Categorized under ${props.category}`}
              href={props.categoryUrl}
            >
              {props.category}
            </Link>
          </div>
        )}
      </div>
    </article>
  );
};

export const ArticleMeta = (props: { children: React.ReactNode }) => {
  const children = Children.toArray(props.children)
    .filter(Boolean)
    .map((child, i) => {
      return (
        <span className={styles.metaItem} key={i} data-zds>
          {child}
        </span>
      );
    });
  return (
    <AvatarText kind="light">
      <span className={styles.meta} data-zds>
        {children}
      </span>
    </AvatarText>
  );
};

export const ArticleReadTime = (props: { minutes: number }) => {
  return <div data-zds>{props.minutes} min read</div>;
};

export const ArticleDate = (props: { date: Dateable }) => {
  return <DateTime date={props.date} />;
};
